import React from "react";
import { Row, Col, Label, Input, FormFeedback, FormGroup, InputGroup, InputGroupText } from "reactstrap";
import { Controller, Control, FieldErrors, useWatch } from "react-hook-form";
import Select from "react-select";
import CommonFlatpickr from "./CommonDatePicker";
import CommonTimePicker from "./CommonTimePicker";
import { isPreviewableFile } from "helpers/constant";

/* ---------- Types ---------- */

export type FieldType =
  | "text"
  | "textarea"
  | "email"
  | "number"
  | "date"
  | "flatpickr"
  | "select"
  | "file"
  | "checkbox"
  | "radio"
  | "time"
  | "password";

export interface SelectOption {
  label: string | number;
  value: string | number;
}

export interface FormFieldConfig {
  /* ---- layout / section ---- */
  sectionTitle?: string; // for headers
  name: string; // optional to allow section headers

  /* ---- input config ---- */
  label?: string;
  type?: FieldType | undefined;
  uppercase?: boolean;
  placeholder?: string;
  description?: string;
  required?: boolean;
  rules?: Record<string, unknown>;

  /* ---- select specific ---- */
  options?: SelectOption[];
  optionsProps?: Record<string, unknown>;
  mutipleAllow?: boolean;
  isClearable?: boolean;

  /* ---- state ---- */
  disabled?: boolean;
  multiple?: boolean;
  existingFiles?: any[]; // 👈 add this
  onExistingDelete?: (id: number, type: string) => void;
  onExistingView?: (file: any) => void;

  onChangeExtra?: (
    selected: any,
    helpers: { setValue?: any; getValues?: any },
  ) => void;
}

interface CommonFormProps {
  fields: FormFieldConfig[];
  control: Control<any>;
  errors?: FieldErrors;
  columns?: number;
  searchInput?: boolean;
  openPreview?: (file: File) => void;
  disableFuture?: boolean;
  setValue?: any;
  getValues?: any;
  parentClass?: string;
  childClass?: string;
  labelShow?: boolean;
}

/* ---------- Component ---------- */

const CommonForm: React.FC<CommonFormProps> = ({
  fields,
  control,
  errors,
  columns = 2,
  searchInput = false,
  openPreview,
  setValue,
  getValues,
  parentClass,
  childClass,
  labelShow = true,
}) => {
  const [showPassword, setShowPassword] = React.useState<Record<string, boolean>>({});
  const columnWidth = 12 / columns;
  const fileInputRefs = React.useRef<Record<string, HTMLInputElement | null>>(
    {},
  );
  const watchedValues = useWatch({ control });

  const togglePasswordVisibility = (fieldName: string) => {
    setShowPassword((prev) => ({
      ...prev,
      [fieldName]: !prev[fieldName],
    }));
  };

  return (
    <Row className={`gy-3 form_input ${parentClass}`}>
      {fields.map((field, index) => {
        const watchedFile =
          field.type === "file" && field.name
            ? watchedValues?.[field.name]
            : null;

        const hasError = field.name ? !!errors?.[field.name] : false;
        const fieldName = field.name as string;

        /* ----------------------------------------
           Section Header (no name)
        ---------------------------------------- */
        if (!field.name) {
          return (
            <Col md={12} key={`section-${index}`} className="mt-2">
              <h6 className="fw-bold text-dark">{field.sectionTitle}</h6>
            </Col>
          );
        }

        return (
          <Col
            md={columnWidth}
            key={field.name}
            className={`mb-2  position-relative ${searchInput ? "px-0" : ""} ${childClass}`}
          >
            {/* -------- Date / Flatpickr -------- */}
            {field.type === "date" || field.type === "flatpickr" ? (
              <CommonFlatpickr
                control={control}
                name={field.name}
                label={field.label}
                placeholder={field.placeholder}
                rules={{
                  ...(field.required && {
                    required: `${field.label} is required`,
                  }),
                  ...field.rules,
                }}
                errors={errors}
                options={field.optionsProps}
                description={field.description}
                disabled={field.disabled}
              />
            ) : field.type === "time" ? (
              /* -------- 24-Hour 2-Column Time Picker -------- */
              <CommonTimePicker
                control={control}
                name={field.name}
                label={field.label}
                placeholder={field.placeholder || "--:--"}
                rules={{
                  ...(field.required && {
                    required: `${field.label} is required`,
                  }),
                  ...field.rules,
                }}
                errors={errors}
                description={field.description}
                disabled={field.disabled}
              />
            ) : field.type === "select" ? (
              /* -------- React Select -------- */
              <>
              {labelShow && (
                <Label className={field.description ? "mb-0" : "mb-2"}>
                  {field.label}
                  {field.required && <span className="text-danger"> *</span>}
                </Label>
              )}

                <Controller
                  name={field.name}
                  control={control}
                  rules={{
                    ...(field.required && {
                      required: `${field.label} is required`,
                    }),
                    ...field.rules, // ✅ THIS IS THE KEY FIX
                  }}
                  render={({ field: controllerField }) => (
                    <Select
                      {...controllerField}
                      options={field.options}
                      placeholder={field.placeholder || "Select..."}
                      isMulti={field.mutipleAllow}
                      isDisabled={field.disabled}
                      isClearable={field.isClearable !== false}
                      menuPortalTarget={document.body}
                      className="common-select"
                      classNamePrefix="common-select"
                      styles={{
                        control: (base: any, state: any) => ({
                          ...base,
                          minHeight: "25px",
                          height: "auto",
                          borderColor: hasError
                            ? "#dc3545"
                            : state.isFocused
                              ? "#86b7fe"
                              : base.borderColor,
                        }),
                        menuPortal: (base: any) => ({ ...base, zIndex: 9999 }),
                        valueContainer: (base: any) => ({
                          ...base,
                          padding: "0 6px",
                          fontSize: "12px",
                        }),
                        input: (base: any) => ({
                          ...base,
                          margin: 0,
                          padding: 0,
                        }),
                        indicatorsContainer: (base: any) => ({
                          ...base,
                          height: "25px",
                        }),
                        option: (base: any, state: any) => ({
                          ...base,
                          fontSize: "12px",
                          padding: "6px 10px",
                          height: "25px",
                          display: "flex",
                          alignItems: "center",
                          backgroundColor: state.isSelected
                            ? "#1e6b91"
                            : state.isFocused
                              ? "#e6f0ff"
                              : "white",
                          color: state.isSelected ? "white" : "black",
                        }),
                      }}
                      value={
                        field.mutipleAllow
                          ? field.options?.filter((opt) =>
                              (
                                controllerField.value as (string | number)[]
                              )?.includes(opt.value),
                            )
                          : field.options?.find((opt) => {
                              const value = controllerField.value;

                              if (Array.isArray(value)) {
                                return opt.value === value[0];
                              }

                              // ✅ Normal selects (most fields)
                              return opt.value === value;
                            }) || null
                      }
                      onChange={(selected: any) => {
                        if (field.mutipleAllow) {
                          const value = selected
                            ? (selected as SelectOption[]).map((i) => i.value)
                            : [];

                          if (setValue) {
                            setValue(field.name, value, {
                              shouldValidate: true,
                              shouldDirty: true,
                            });
                          } else {
                            controllerField.onChange(value);
                          }

                          field.onChangeExtra?.(selected, {
                            setValue,
                            getValues,
                          });
                        } else {
                          if (setValue) {
                            setValue(
                              field.name,
                              selected ? selected.value : "",
                              {
                                shouldValidate: true,
                                shouldDirty: true,
                              },
                            );
                          } else {
                            controllerField.onChange(
                              selected ? selected.value : "",
                            );
                          }

                          field.onChangeExtra?.(selected, {
                            setValue,
                            getValues,
                          });
                        }
                      }}
                    />
                  )}
                />

                {errors?.[field.name] && (
                  <FormFeedback className="d-block position-absolute">
                    {errors[field.name]?.message as string}
                  </FormFeedback>
                )}
              </>
            ) : (
              /* -------- Default Input -------- */
              <>
                {/* <Label className={field.description ? "mb-0" : "mb-2"}>
                  {field.label}
                  {field.required && <span className="text-danger"> *</span>}
                </Label> */}

                {field.type !== "checkbox" && field.type !== "radio" && (
                  <Label
                    className={
                      field.description
                        ? "mb-0 d-flex align-items-center gap-2"
                        : "mb-2"
                    }
                  >
                    {field.label}
                    {field.required && <span className="text-danger"> *</span>}
                    {field.type === "file" &&
                      field.description &&
                      isPreviewableFile(watchedFile) && (
                        <div className="d-flex gap-1">
                          <i
                            className="ri-eye-line text-primary cursor-pointer"
                            title="View document"
                            onClick={() => openPreview?.(watchedFile)}
                            style={{ cursor: "pointer" }}
                          />
                          <i
                            className="ri-delete-line text-danger cursor-pointer"
                            title="Delete document"
                            onClick={() => openPreview?.(watchedFile)}
                            style={{ cursor: "pointer" }}
                          />
                        </div>
                      )}
                  </Label>
                )}

                {field.description && (
                  <small className="text-muted d-block mb-1">
                    {field.description}
                  </small>
                )}

                <Controller
                  name={field.name}
                  control={control}
                  rules={{
                    ...(field.required && {
                      required: `${field.label} is required`,
                    }),
                    ...field.rules,
                  }}
                  render={({ field: controllerField }) => {
                    /* -------- FILE INPUT + VIEW + DELETE -------- */
                    if (field.type === "file") {
                      return (
                        <>
                          <Input
                            type="file"
                            bsSize="sm"
                            multiple={field.multiple} // ✅ enable multiple
                            invalid={!!errors?.[fieldName]}
                            disabled={field.disabled}
                            innerRef={(el) => {
                              if (
                                el instanceof HTMLInputElement &&
                                el.type === "file"
                              ) {
                                fileInputRefs.current[fieldName] = el;
                              }
                            }}
                            onChange={(
                              e: React.ChangeEvent<HTMLInputElement>,
                            ) => {
                              if (field.multiple) {
                                const newFiles = e.target.files
                                  ? Array.from(e.target.files)
                                  : [];

                                const existingFiles = Array.isArray(
                                  controllerField.value,
                                )
                                  ? controllerField.value
                                  : [];

                                controllerField.onChange([
                                  ...existingFiles,
                                  ...newFiles,
                                ]);

                                e.target.value = "";
                              } else {
                                const file = e.target.files?.[0] || null;
                                controllerField.onChange(file);
                              }
                            }}
                          />

                          {/* -------- MULTIPLE FILE PREVIEW -------- */}
                          {field.multiple && Array.isArray(watchedFile) && (
                            <div className="mt-1">
                              {watchedFile.map((file: File, index: number) => (
                                <div
                                  key={index}
                                  className="d-flex align-items-center gap-2"
                                >
                                  <span className="small text-muted text-truncate">
                                    {file.name}
                                  </span>

                                  {isPreviewableFile(file) && (
                                    <i
                                      className="ri-eye-line text-primary cursor-pointer"
                                      title="View document"
                                      onClick={() => openPreview?.(file)}
                                    />
                                  )}

                                  <i
                                    className="ri-delete-bin-line text-danger cursor-pointer"
                                    title="Delete document"
                                    onClick={() => {
                                      const updatedFiles = watchedFile.filter(
                                        (_: File, i: number) => i !== index,
                                      );

                                      setValue(
                                        field.name as any,
                                        updatedFiles,
                                        {
                                          shouldDirty: true,
                                          shouldValidate: true,
                                        },
                                      );
                                    }}
                                  />
                                </div>
                              ))}
                            </div>
                          )}

                          {/* -------- SINGLE FILE PREVIEW -------- */}
                          {!field.multiple && watchedFile instanceof File && (
                            <div className="d-flex align-items-center gap-2 mt-1">
                              <span className="small text-muted text-truncate">
                                {watchedFile.name}
                              </span>

                              {isPreviewableFile(watchedFile) && (
                                <i
                                  className="ri-eye-line text-primary cursor-pointer"
                                  title="View document"
                                  onClick={() => openPreview?.(watchedFile)}
                                />
                              )}

                              <i
                                className="ri-delete-bin-line text-danger cursor-pointer"
                                title="Delete document"
                                onClick={() => {
                                  setValue(field.name as any, null, {
                                    shouldDirty: true,
                                    shouldValidate: true,
                                  });

                                  const input =
                                    fileInputRefs.current[fieldName];
                                  if (input) input.value = "";
                                }}
                              />
                            </div>
                          )}

                          {field.existingFiles &&
                            field.existingFiles.length > 0 && (
                              <div className="mt-2">
                                <small className="fw-semibold text-primary d-block mb-1">
                                  Existing Attachments
                                </small>

                                {field.existingFiles.map((file: any) => (
                                  <div
                                    key={file.attachmentid}
                                    className="d-flex align-items-center gap-2 mb-1"
                                  >
                                    <span className="small text-muted">
                                      {file.type?.toUpperCase()}
                                    </span>

                                    <span className="small text-truncate">
                                      {file.name}
                                    </span>

                                    <i
                                      className="ri-eye-line text-primary cursor-pointer"
                                      title="View"
                                      onClick={() =>
                                        field.onExistingView?.(file)
                                      }
                                    />

                                    <i
                                      className="ri-delete-bin-line text-danger cursor-pointer"
                                      title="Delete"
                                      onClick={() =>
                                        field.onExistingDelete?.(
                                          file.attachmentid,
                                          file.type,
                                        )
                                      }
                                    />
                                  </div>
                                ))}
                              </div>
                            )}
                        </>
                      );
                    }

                    /* -------- CHECKBOX (Label Left, Checkbox Right) -------- */
                    if (field.type === "checkbox") {
                      return (
                        <div className="gap-2 d-flex align-items-center">
                          <Input
                            id={fieldName}
                            type="checkbox"
                            checked={!!controllerField.value}
                            disabled={field.disabled}
                            invalid={!!errors?.[fieldName]}
                            onChange={(e) =>
                              controllerField.onChange(e.target.checked)
                            }
                          />
                          <Label htmlFor={fieldName} className="mb-0">
                            {field.label}
                            {field.required && (
                              <span className="text-danger"> *</span>
                            )}
                          </Label>
                        </div>
                      );
                    }

                    /* -------- RADIO -------- */
                    if (field.type === "radio") {
                      return (
                        <div>
                          <Label
                            htmlFor={fieldName}
                            className="mb-1 text-muted"
                          >
                            {field.label}
                            {field.required && (
                              <span className="text-danger"> *</span>
                            )}
                          </Label>
                          <div>
                            {field.options?.map((opt) => (
                              <FormGroup
                                check
                                inline
                                key={opt.value}
                                className="mb-0 me-3"
                              >
                                <Input
                                  type="radio"
                                  id={`${field.name}_${opt.value}`}
                                  name={controllerField.name}
                                  value={opt.value}
                                  checked={controllerField.value === opt.value}
                                  disabled={field.disabled}
                                  invalid={!!errors?.[fieldName]}
                                  onChange={() =>
                                    controllerField.onChange(opt.value)
                                  }
                                />
                                <Label
                                  check
                                  htmlFor={`${field.name}_${opt.value}`}
                                >
                                  {opt.label}
                                </Label>
                              </FormGroup>
                            ))}
                          </div>
                        </div>
                      );
                    }
                    /* -------- DEFAULT INPUTS -------- */
                    if (field.type === "password") {
                      return (
                        <InputGroup className={hasError ? "is-invalid" : ""}>
                          <Input
                            {...controllerField}
                            type={showPassword[fieldName] ? "text" : "password"}
                            placeholder={field.placeholder}
                            invalid={!!errors?.[fieldName]}
                            bsSize="sm"
                            disabled={field.disabled}
                            onChange={(e) => {
                              let value: any = e.target.value.replace(/^\s+/, "");

                              if (field.uppercase) value = value.toUpperCase();

                              if (field.type === "number") {
                                value = value === "" ? null : Number(value);
                              }

                              if (setValue) {
                                setValue(field.name, value, {
                                  shouldValidate: true,
                                  shouldDirty: true,
                                });
                              } else {
                                controllerField.onChange(value);
                              }
                            }}
                            onKeyDown={(e) => {
                              // Prevent leading space
                              if (e.key === " " && !controllerField.value) {
                                e.preventDefault();
                              }

                              // Prevent invalid characters in number input
                              if (
                                field.type === "number" &&
                                ["e", "E", "+", "-"].includes(e.key)
                              ) {
                                e.preventDefault();
                              }
                            }}
                          />
                          <InputGroupText
                            className="bg-white border-start-0"
                            style={{ cursor: "pointer" }}
                            onClick={() => togglePasswordVisibility(fieldName)}
                          >
                            <i
                              className={`${
                                showPassword[fieldName]
                                  ? "ri-eye-off-line"
                                  : "ri-eye-line"
                              } text-primary`}
                            />
                          </InputGroupText>
                        </InputGroup>
                      );
                    }

                    return (
                      <Input
                        {...controllerField}
                        type={
                          field.type === "textarea" ||
                          field.type === "email" ||
                          field.type === "number"
                            ? field.type
                            : "text"
                        }
                        placeholder={field.placeholder}
                        invalid={!!errors?.[fieldName]}
                        bsSize="sm"
                        disabled={field.disabled}
                        onChange={(e) => {
                          let value: any = e.target.value.replace(/^\s+/, "");

                          if (field.uppercase) value = value.toUpperCase();

                          if (field.type === "number") {
                            value = value === "" ? null : Number(value);
                          }

                          if (setValue) {
                            setValue(field.name, value, {
                              shouldValidate: true,
                              shouldDirty: true,
                            });
                          } else {
                            controllerField.onChange(value);
                          }
                        }}
                        onKeyDown={(e) => {
                          // Prevent leading space
                          if (e.key === " " && !controllerField.value) {
                            e.preventDefault();
                          }

                          // Prevent invalid characters in number input
                          if (
                            field.type === "number" &&
                            ["e", "E", "+", "-"].includes(e.key)
                          ) {
                            e.preventDefault();
                          }
                        }}
                      />
                    );
                  }}
                />

                {errors?.[field.name] && (
                  <FormFeedback className="d-block">
                    {errors[field.name]?.message as string}
                  </FormFeedback>
                )}
              </>
            )}
          </Col>
        );
      })}
    </Row>
  );
};

export default CommonForm;
